import { formatWidgetDataResponse } from './utils/dashboardWidgetUtils';
import * as t from './actionTypes';

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Widget Actions (Add)                                                                 */
/*------------------------------------------------------------------------------------------------*/
/**
 * addWidget
 * Add a widget to Dashboard
 * @param { Object } widgetOptions: Options for widget
 *    {
 *      x: {Integer} x coordinate on grid.
 *      y: {Integer} y coordinate on grid.
 *      w: {Integer} Width of widget (number of columns).
 *      h: {Integer} Weight of widget (number of rows).
 *      maxW: {Integer} Max-width of widget.
 *      maxH: {Integer} Max-height of widget.
 *      static: {Boolean} Widget cannot be moved or resized.
 *      isDraggable: {Boolean} Widget can be dragged/moved.
 *      isResizable: {Boolean} Widget can be resized.
 *      widget: { Object } Config of dashboard widget.
 *    }
 * @returns { Object } action, new layout panel and new widget.
 */
export const addWidget = (widgetOptions) => ({
  type: t.ADD_WIDGET,
  widgetOptions,
});

export const addWidgetResponse = ({ layout, widgets }) => ({
  type: t.ADD_WIDGET_RESPONSE,
  layout,
  widgets,
});

export const addWidgetError = (error) => ({
  type: t.ADD_WIDGET_ERROR,
  error,
});

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Widget Actions (Delete)                                                              */
/*------------------------------------------------------------------------------------------------*/
export const deleteWidget = (deletedWidgetId) => ({
  type: t.DELETE_WIDGET,
  deletedWidgetId,
});

export const deleteWidgetResponse = ({ layout, widgets }) => ({
  type: t.DELETE_WIDGET_RESPONSE,
  layout,
  widgets,
});

export const deleteWidgetError = (error) => ({
  type: t.DELETE_WIDGET_ERROR,
  error,
});

/**
 * saveWidget
 * Saves a dashboard widget
 * @param { Object } widgetOptions: Options for widget. See addWidget for widgetOptions description.
 * @returns { Object } action and updated widget.
 */
export const saveWidget = (widgetOptions) => ({
  type: t.SAVE_WIDGET,
  widgetOptions,
});

export const saveWidgetResponse = ({ widgets }) => ({
  type: t.SAVE_WIDGET_RESPONSE,
  widgets,
});

export const saveWidgetError = (error) => ({
  type: t.SAVE_WIDGET_ERROR,
  error,
});

export const layoutChange = (layout) => ({
  type: t.LAYOUT_CHANGE,
  fetchingDashboard: false,
  layoutLoading: false,
  layout,
});

/*------------------------------------------------------------------------------------------------*/
/* Campaigns Table Widget Actions                                                                 */
/*------------------------------------------------------------------------------------------------*/
export const fetchCampaigns = ({ fetchParams, widget }) => ({
  type: t.FETCH_CAMPAIGNS,
  fetchParams,
  widget,
});

export const fetchCampaignsInit = ({ campaignFetchParamData, fetchingCampaignsTableIds }) => ({
  type: t.FETCH_CAMPAIGNS_INIT,
  campaignFetchParamData,
  fetchingCampaignsTableIds,
});

export const fetchCampaignsError = (error) => ({
  type: t.FETCH_CAMPAIGNS_ERROR,
  fetchingCampaignsTableIds: [],
  error,
});

export const fetchCampaignsResponse = ({ dashboardData, fetchingCampaignsTableIds, widget }) => ({
  type: t.FETCH_CAMPAIGNS_RESPONSE,
  fetchingCampaignsTableIds,
  dashboardData,
  widget,
});

/*------------------------------------------------------------------------------------------------*/
/* Add/Edit Modal Actions                                                                         */
/*------------------------------------------------------------------------------------------------*/
export const openAddEditModal = (selectedWidget) => ({
  type: t.OPEN_ADD_EDIT_MODAL,
  modalState: true,
  addMode: !selectedWidget,
  selectedWidget: Object.assign({}, JSON.parse(JSON.stringify(selectedWidget || { name: '' }))),
});

export const closeAddEditModal = () => ({
  type: t.CLOSE_ADD_EDIT_MODAL,
  modalState: false,
  addMode: false,
  selectedWidget: {},
});

export const changeSelectedWidget = (selectedWidget) => ({
  type: t.CHANGE_SELECTED_WIDGET,
  selectedWidget: Object.assign({}, selectedWidget),
});

/*------------------------------------------------------------------------------------------------*/
/* Delete Modal Actions                                                                           */
/*------------------------------------------------------------------------------------------------*/
export const openDeleteModal = (selectedWidgetId) => ({
  type: t.OPEN_DELETE_MODAL,
  modalState: true,
  selectedWidgetId,
});

export const closeDeleteModal = () => ({
  type: t.CLOSE_DELETE_MODAL,
  modalState: false,
  selectedWidget: '',
});

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Settings Modal Actions                                                               */
/*------------------------------------------------------------------------------------------------*/
export const openSettingsModal = () => ({
  type: t.OPEN_SETTINGS_MODAL,
  modalState: true,
});

export const closeSettingsModal = (updatedSettings) => ({
  type: t.CLOSE_SETTINGS_MODAL,
  modalState: false,
  ...updatedSettings,
});

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Settings Actions                                                                     */
/*------------------------------------------------------------------------------------------------*/
export const changeTheme = (theme) => ({
  type: t.CHANGE_THEME,
  theme,
});

export const changeDefaultStartRange = (defaultStartRange /*: string */) => ({
  type: t.CHANGE_DEFAULT_START_RANGE,
  defaultStartRange,
});

export const changeDefaultEndRange = (defaultEndRange /*: string */) => ({
  type: t.CHANGE_DEFAULT_END_RANGE,
  defaultEndRange,
});

/*------------------------------------------------------------------------------------------------*/
/* Date/Time Range Actions                                                                        */
/*------------------------------------------------------------------------------------------------*/
export const changeStartDate = (startDate /*: Moment */) => ({
  type: t.CHANGE_START_DATE,
  startDate,
});

export const changeEndDate = (endDate /*: Moment */) => ({
  type: t.CHANGE_END_DATE,
  endDate,
});

export const changeStartTime = (startTime /*: Moment */) => ({
  type: t.CHANGE_START_TIME,
  startTime,
});

export const changeEndTime = (endTime /*: Moment */) => ({
  type: t.CHANGE_END_TIME,
  endTime,
});

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Fetching Actions                                                                     */
/*------------------------------------------------------------------------------------------------*/
export const fetchDashboard = () => ({
  type: t.FETCH_DASHBOARD,
  fetchingDashboard: true,
  layoutLoading: true,
});

export const fetchDashboardError = (error) => ({
  type: t.FETCH_DASHBOARD_ERROR,
  fetchingDashboard: false,
  error,
});

/**
 * fetchDashboardResponse
 * Fetches data for the dashboard and parses the date and time to display in date picker.
 * @param { Object } response: Response back from the server containing fetched data.
 * @returns { Object } The fetch dashboard reponse action and dashboard response data.
 */
export const fetchDashboardResponse = ({
  campaignFetchParamData,
  defaultStartRange,
  defaultEndRange,
  endDate,
  endTime,
  layout,
  startDate,
  startTime,
  theme,
  widgets,
}) => ({
  type: t.FETCH_DASHBOARD_RESPONSE,
  campaignFetchParamData,
  defaultStartRange,
  defaultEndRange,
  endDate,
  endTime,
  fetchingDashboard: false,
  layout,
  startDate,
  startTime,
  theme,
  widgets,
});

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Fetching Widget Data Actions                                                         */
/*------------------------------------------------------------------------------------------------*/
export const fetchDashboardData = () => ({
  type: t.FETCH_DASHBOARD_DATA,
  fetchingData: true,
});

export const fetchDashboardDataInit = (dashboardData) => ({
  type: t.FETCH_DASHBOARD_DATA_INIT,
  dashboardData,
});

export const fetchDashboardDataError = (error) => ({
  type: t.FETCH_DASHBOARD_DATA_ERROR,
  fetchingData: false,
  error,
});

export const fetchDashboardDataWidgetError = (error) => ({
  type: t.FETCH_DASHBOARD_DATA_WIDGET_ERROR,
  error,
});

export const fetchDashboardDataComplete = () => ({
  type: t.FETCH_DASHBOARD_DATA_COMPLETE,
  fetchingData: false,
});

/**
 * fetchDashboardDataResponse
 * Fetches data for the dashboard and parses the date and time to display in date picker.
 * @param { Object } dashboardData: The request params.
 * @returns { Object } The fetch dashboard reponse action and dashboard response data.
 */
export const fetchDashboardDataResponse = (dashboardData) => ({
  type: t.FETCH_DASHBOARD_DATA_RESPONSE,
  dashboardData,
});

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Saving Actions                                                                       */
/*------------------------------------------------------------------------------------------------*/
/**
 * saveDashboard
 * Saves data for dashboard and formats date and time to saving format.
 * @param { Object } dashboardData: The dashboard data to save.
 * @returns { Object } The save dashboard action.
 */
export const saveDashboard = (dashboardData) => ({
  type: t.SAVE_DASHBOARD,
  dashboardData,
});

export const saveDashboardError = (error) => ({
  type: t.SAVE_DASHBOARD_ERROR,
  error,
});

export const saveDashboardResponse = (dashboardResponse) => ({
  type: t.SAVE_DASHBOARD_RESPONSE,
  ...dashboardResponse,
});

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Mock Actions                                                                         */
/*------------------------------------------------------------------------------------------------*/
export const setDashboard = ({ widgets, settings }) =>
  fetchDashboardResponse(formatWidgetDataResponse(widgets, settings));

export const resetDashboard = () => ({
  type: t.RESET_DASHBOARD,
});
